import React, { useState } from 'react';
import { Menu, Bell, Moon, Sun, ChevronDown, Search, Building2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useOrganization } from '../contexts/OrganizationContext';
import GlobalSearch from './GlobalSearch';

const Header: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { signOut, user } = useAuth();
  const { currentOrganization, userOrganizations, switchOrganization, userRole } = useOrganization();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Display user info from auth and organization context
  // Use userRole from OrganizationContext (from organization_members table) instead of user_metadata
  const displayUser = user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    email: user.email || '',
    role: (userRole || 'owner') as any, // Use role from organization_members table
  } : null;

  const handleSignOut = () => {
    signOut();
    setUserMenuOpen(false);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    // Open search with Ctrl/Cmd + K
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setSearchOpen(true);
    }
  };

  // Add global keyboard listener
  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Close organization menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgMenuOpen && !(event.target as Element).closest('.org-switcher')) {
        setOrgMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [orgMenuOpen]);

  return (
    <>
      <header className="bg-white dark:bg-gray-800 shadow-sm z-10">
        <div className="flex items-center justify-between px-3 py-2 md:px-4 md:py-3">
          <div className="flex items-center md:hidden">
            <button 
              onClick={toggleSidebar}
              className="p-2 text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-white 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
              aria-label="Toggle navigation menu"
              aria-expanded={!isCollapsed}
              aria-controls="main-navigation"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 mx-2 md:ml-4">
            <div className="relative">
              <button
                onClick={() => setSearchOpen(true)}
                onKeyDown={handleSearchKeyDown}
                className="w-full max-w-md px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 
                          focus:ring-blue-500 dark:focus:ring-blue-400 text-left flex items-center justify-between
                          hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                aria-label="Open global search"
                aria-keyshortcuts="Control+K"
              >
                <div className="flex items-center">
                  <Search className="w-4 h-4 mr-2" />
                  <span className="hidden xs:inline">Search everything...</span>
                  <span className="xs:hidden">Search...</span>
                </div>
                <div className="hidden md:flex items-center space-x-1">
                  <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-xs">
                    {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}
                  </kbd>
                  <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded text-xs">K</kbd>
                </div>
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Organization Switcher */}
            {userOrganizations.length > 1 && (
              <div className="relative org-switcher">
                <button
                  onClick={() => setOrgMenuOpen(!orgMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg
                            hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  aria-label="Switch organization"
                >
                  <Building2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 max-w-32 truncate">
                    {currentOrganization?.name || 'Select Org'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
                
                {orgMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Organizations
                      </div>
                      {userOrganizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => {
                            switchOrganization(org.id);
                            setOrgMenuOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm transition-colors
                                    ${currentOrganization?.id === org.id 
                                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Building2 className="w-4 h-4" />
                            <div>
                              <div className="font-medium">{org.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {org.subscription_tier} plan
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <button className="p-2 text-gray-600 dark:text-gray-200 hover:text-gray-800 
                            dark:hover:text-white rounded-full hover:bg-gray-100 
                            dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label="View notifications">
              <Bell className="w-5 h-5" />
            </button>
            
            <button 
              onClick={toggleTheme}
              className="p-2 text-gray-600 dark:text-gray-200 hover:text-gray-800 
                        dark:hover:text-white rounded-full hover:bg-gray-100 
                        dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {/* User Menu */}
            <div className="relative">
              <button 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-1"
                aria-label="User menu"
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                <div className="w-8 h-8 relative overflow-hidden rounded-full flex items-center justify-center shadow-lg">
                  {/* Dynamic Chevron Logo */}
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-40 transform -translate-y-1/3 -rotate-12 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-70 transform -rotate-6 rounded-md shadow-lg"></div>
                    <div className="absolute w-2/3 h-1/4 bg-gradient-to-br from-blue-800 to-cyan-400 opacity-100 transform translate-y-1/3 rotate-12 rounded-md shadow-lg"></div>
                  </div>
                </div>
                    {displayUser && (
                      <div className="hidden md:block text-left">
                    <div className="flex items-center space-x-2">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {displayUser.name}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {displayUser.role}
                    </div>
                  </div>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
              
              {userMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setUserMenuOpen(false)}
                  />
                  
                  {/* Dropdown */}
                  <div 
                    className="absolute right-0 mt-3 w-72 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                  >
                    {/* Header */}
                    <div className="px-5 py-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-850 border-b border-gray-200/50 dark:border-gray-700/50">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        My Account
                      </p>
                    </div>

                    {/* Current User Info */}
                    {displayUser && (
                      <div className="py-2">
                        <div className="px-5 py-3 text-sm flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500">
                          <div className="flex-1">
                            <div className="font-medium text-gray-700 dark:text-gray-200">
                              {displayUser.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-1">
                              {displayUser.role}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Footer with Sign Out */}
                    <div className="px-3 py-3 bg-gray-50 dark:bg-gray-850 border-t border-gray-200/50 dark:border-gray-700/50">
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 
                                 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 
                                 rounded-lg border border-red-200 dark:border-red-800/30
                                 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
                                 transition-all duration-150 group"
                        role="menuitem"
                      >
                        <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Global Search Modal */}
      <GlobalSearch 
        isOpen={searchOpen} 
        onClose={() => setSearchOpen(false)} 
      />
    </>
  );
};

export default Header;